import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type PaymentProviderSettings } from '../services/api';

export function PaymentProviderDebugSettings({ onSaved }: { onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<PaymentProviderSettings | null>(null);
  const [provider, setProvider] = useState<PaymentProviderSettings['provider']>('adyen');
  const [mode, setMode] = useState<PaymentProviderSettings['mode']>('mock');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    adminApi.getPaymentProvider().then(value => {
      if (!active) return;
      setSettings(value);
      setProvider(value.provider);
      setMode(value.mode);
    }).catch(() => { if (active) setError(t('paymentProviderDebug.loadError')); });
    return () => { active = false; };
  }, [t]);

  const needsConfirmation = provider === 'adyen' && mode === 'sandbox';
  const disabled = !settings?.editable || saving;
  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const value = await adminApi.updatePaymentProvider({ provider, mode, ...(needsConfirmation ? { confirmation } : {}) });
      setSettings(value);
      setProvider(value.provider);
      setMode(value.mode);
      setConfirmation('');
      setSaved(true);
      await onSaved();
    } catch { setError(t('paymentProviderDebug.saveError')); }
    finally { setSaving(false); }
  };

  return <section aria-labelledby="payment-provider-title">
    <h5 id="payment-provider-title">{t('paymentProviderDebug.title')}</h5>
    <p className="text-muted">{t('paymentProviderDebug.description')}</p>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {saved && <div className="alert alert-success" role="status">{t('paymentProviderDebug.saved')}</div>}
    {!settings && !error && <p role="status">{t('paymentProviderDebug.loading')}</p>}
    {settings && <>
      <div className="alert alert-info" role="note">{t('paymentProviderDebug.productionInfo')}</div>
      {provider === 'ping' && <div className="alert alert-warning" role="note">{t('paymentProviderDebug.pingDeposit')}</div>}
      {provider === settings.provider && mode === settings.mode && settings.unavailableReason === 'payment_environment_unavailable' && <div className="alert alert-warning text-break" role="note">
        {settings.pingConfigurationIssues?.length ? <>
          <p>{t('paymentProviderDebug.pingConfigurationUnavailable')}</p>
          <ul>{settings.pingConfigurationIssues.map(issue => <li key={issue}>{t(`paymentProviderDebug.pingConfigurationIssues.${issue}`)}</li>)}</ul>
          <p className="mb-0">{t('paymentProviderDebug.restartApi')}</p>
        </> : t('paymentProviderDebug.environmentUnavailable')}
      </div>}
      <p>{t('paymentProviderDebug.current', { provider: t(`paymentProviderDebug.providers.${settings.provider}`), mode: t(`paymentProviderDebug.modes.${settings.mode}`) })}</p>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="paymentProvider" className="form-label">{t('paymentProviderDebug.providerLabel')}</label>
          <select id="paymentProvider" className="form-select form-select-lg" value={provider} disabled={disabled} onChange={event => { setProvider(event.target.value as PaymentProviderSettings['provider']); setSaved(false); setConfirmation(''); }}>
            {settings.availableProviders.map(value => <option key={value} value={value}>{t(`paymentProviderDebug.providers.${value}`)}</option>)}
          </select>
        </div>
        <div className="col-md-6">
          <label htmlFor="paymentProviderMode" className="form-label">{t('paymentProviderDebug.modeLabel')}</label>
          <select id="paymentProviderMode" className="form-select form-select-lg" value={mode} disabled={disabled} onChange={event => { setMode(event.target.value as PaymentProviderSettings['mode']); setSaved(false); setConfirmation(''); }}>
            {Array.from(new Set([...settings.availableModes, ...(!settings.editable ? [settings.mode] : [])])).map(value => <option key={value} value={value}>{t(`paymentProviderDebug.modes.${value}`)}</option>)}
          </select>
        </div>
        {needsConfirmation && settings.editable && <div className="col-12">
          <label htmlFor="paymentProviderConfirmation" className="form-label">{t('paymentProviderDebug.confirmSandbox')}</label>
          <input id="paymentProviderConfirmation" className="form-control form-control-lg" value={confirmation} disabled={disabled} onChange={event => setConfirmation(event.target.value)} />
          <div className="form-text">{t('paymentProviderDebug.confirmHint', { value: settings.sandboxConfirmation })}</div>
        </div>}
        <div className="col-12"><button type="button" className="btn btn-primary btn-lg" disabled={disabled || (needsConfirmation && confirmation !== settings.sandboxConfirmation)} onClick={() => void save()}>{t('paymentProviderDebug.save')}</button></div>
      </div>
    </>}
  </section>;
}
